import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../assets/AdminNavbar.css';
import logo from '../assets/images/jj-tutorial-logo.png.png';
import NotificationBell from './NotificationBell';
import WhatsAppInboxBell from './WhatsAppInboxBell';
import UserMenu from './UserMenu';

const AdminNavbar = ({ activeTab, setActiveTab, user, onLogout }) => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setIsMobileMenuOpen(false);
    if (tab === 'tutors') {
      navigate('/admin/tutors');
    } else if (tab === 'tutees') {
      navigate('/admin/tutees');
    } else if (tab === 'lessons') {
      navigate('/admin/lessons');
    } else if (tab === 'invoices') {
      navigate('/admin/invoices');
    } else if (tab === 'tutee-invoices') {
      navigate('/admin/tutee-invoices');
    } else if (tab === 'claims') {
      navigate('/admin/claims');
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <nav className="admin-navbar" role="navigation" aria-label="Main navigation">
      <div className="navbar-brand">
        <img src={logo} alt="JJ Tutorial logo" className="navbar-logo" />
        <span className="navbar-title">Admin Portal</span>
      </div>

      {isMobile && (
        <button
          className="hamburger-menu"
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation menu"
          aria-expanded={isMobileMenuOpen}
        >
          <span className={`hamburger-icon ${isMobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      )}

      {!isMobile && (
        <>
          <div className="navbar-tabs" role="tablist" aria-label="Main navigation tabs">
            {user?.role === 'admin' && (
              <button
                className={activeTab === 'tutors' ? 'active' : ''}
                onClick={() => handleTabChange('tutors')}
                role="tab"
                aria-selected={activeTab === 'tutors'}
                aria-label="Tutors"
              >
                Tutors
              </button>
            )}
            {(user?.role === 'admin' || user?.role === 'finance') && (
              <button
                className={activeTab === 'tutees' ? 'active' : ''}
                onClick={() => handleTabChange('tutees')}
                role="tab"
                aria-selected={activeTab === 'tutees'}
                aria-label="Tutees"
              >
                Tutees
              </button>
            )}
            {(user?.role === 'admin' || user?.role === 'finance') && (
              <button
                className={activeTab === 'lessons' ? 'active' : ''}
                onClick={() => handleTabChange('lessons')}
                role="tab"
                aria-selected={activeTab === 'lessons'}
                aria-label="Lessons"
              >
                Lessons
              </button>
            )}
            {(user?.role === 'admin' || user?.role === 'finance') && (
              <button
                className={activeTab === 'invoices' ? 'active' : ''}
                onClick={() => handleTabChange('invoices')}
                role="tab"
                aria-selected={activeTab === 'invoices'}
                aria-label="Tutor Invoice Management"
              >
                Tutor Invoices
              </button>
            )}
            {(user?.role === 'admin' || user?.role === 'finance') && (
              <button
                className={activeTab === 'tutee-invoices' ? 'active' : ''}
                onClick={() => handleTabChange('tutee-invoices')}
                role="tab"
                aria-selected={activeTab === 'tutee-invoices'}
                aria-label="Tutee Invoices"
              >
                Tutee Invoices
              </button>
            )}
            <button
              className={activeTab === 'claims' ? 'active' : ''}
              onClick={() => handleTabChange('claims')}
              role="tab"
              aria-selected={activeTab === 'claims'}
              aria-label="Claims Management"
            >
              Claims Management
            </button>
          </div>

          <div className="navbar-user">
            {user?.role === 'admin' && <WhatsAppInboxBell />}
            <NotificationBell />
            <UserMenu
              user={user}
              onLogout={onLogout}
              onNavigate={handleNavigate}
              showActivityLog={user?.role === 'admin'}
            />
          </div>
        </>
      )}

      {isMobile && !isMobileMenuOpen && (
        <div className="navbar-user-mobile">
          {user?.role === 'admin' && <WhatsAppInboxBell />}
          <NotificationBell />
        </div>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <>
          <div
            className={`mobile-drawer-overlay ${isMobileMenuOpen ? 'open' : ''}`}
            onClick={toggleMobileMenu}
            aria-hidden={!isMobileMenuOpen}
          />
          <div
            className={`mobile-drawer ${isMobileMenuOpen ? 'open' : ''}`}
            role="dialog"
            aria-label="Mobile navigation menu"
            aria-modal="true"
          >
            <div className="mobile-drawer-header">
              <div className="mobile-user-info">
                <span className="mobile-user-name">{user?.name || 'User'}</span>
                <span className="mobile-user-role">
                  {user?.role === 'admin' ? 'Administrator' : 'Finance'}
                </span>
              </div>
              <button
                className="mobile-drawer-close"
                onClick={toggleMobileMenu}
                aria-label="Close navigation menu"
              >
                ✕
              </button>
            </div>

            <nav className="mobile-drawer-nav" role="navigation" aria-label="Mobile navigation">
              {user?.role === 'admin' && (
                <button
                  className={activeTab === 'tutors' ? 'active' : ''}
                  onClick={() => handleTabChange('tutors')}
                  aria-label="Tutors"
                  aria-current={activeTab === 'tutors' ? 'page' : undefined}
                >
                  Tutors
                </button>
              )}
              {(user?.role === 'admin' || user?.role === 'finance') && (
                <button
                  className={activeTab === 'tutees' ? 'active' : ''}
                  onClick={() => handleTabChange('tutees')}
                  aria-label="Tutees"
                  aria-current={activeTab === 'tutees' ? 'page' : undefined}
                >
                  Tutees
                </button>
              )}
              {(user?.role === 'admin' || user?.role === 'finance') && (
                <button
                  className={activeTab === 'lessons' ? 'active' : ''}
                  onClick={() => handleTabChange('lessons')}
                  aria-label="Lessons"
                  aria-current={activeTab === 'lessons' ? 'page' : undefined}
                >
                  Lessons
                </button>
              )}
              {(user?.role === 'admin' || user?.role === 'finance') && (
                <button
                  className={activeTab === 'invoices' ? 'active' : ''}
                  onClick={() => handleTabChange('invoices')}
                  aria-label="Tutor Invoice Management"
                  aria-current={activeTab === 'invoices' ? 'page' : undefined}
                >
                  Tutor Invoices
                </button>
              )}
              {(user?.role === 'admin' || user?.role === 'finance') && (
                <button
                  className={activeTab === 'tutee-invoices' ? 'active' : ''}
                  onClick={() => handleTabChange('tutee-invoices')}
                  aria-label="Tutee Invoices"
                  aria-current={activeTab === 'tutee-invoices' ? 'page' : undefined}
                >
                  Tutee Invoices
                </button>
              )}
              <button
                className={activeTab === 'claims' ? 'active' : ''}
                onClick={() => handleTabChange('claims')}
                aria-label="Claims Management"
                aria-current={activeTab === 'claims' ? 'page' : undefined}
              >
                Claims Management
              </button>

              {user?.role === 'admin' && (
                <>
                  <div className="mobile-drawer-divider" role="separator" aria-hidden="true"></div>
                  <button
                    onClick={() => handleNavigate('/admin/activity')}
                    aria-label="Activity Log"
                  >
                    Activity Log
                  </button>
                </>
              )}
            </nav>

            <div className="mobile-drawer-footer">
              <button
                className="mobile-logout-btn"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onLogout();
                }}
                aria-label="Logout"
              >
                Logout
              </button>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default AdminNavbar;
